import { useCallback, useEffect, useRef, useState } from 'react';

import { INPUT_DOCUMENT_SAVE_DEBOUNCE_MS } from 'constants/appConstants';
import type { IFarm, IFarmWriteResponse } from 'interfaces/IFarm';
import type { IPaddock } from 'interfaces/IPaddock';
import type { PartialUserProfile } from 'interfaces/IUserProfile';
import type { IZone } from 'interfaces/IZone';
import {
  buildFarmSave,
  farmFormValues,
  paddockFormValues,
  type FarmFormValues,
  type PaddockFormValues,
} from 'utils/farmPayloads';

type FarmPatch = Partial<FarmFormValues>;
type PaddockPatch = Partial<PaddockFormValues>;
interface PaddockEdit {
  farmId: string;
  patch: PaddockPatch;
}
interface Edits {
  farms: Map<string, FarmPatch>;
  paddocks: Map<string, PaddockEdit>;
}

const emptyEdits = (): Edits => ({ farms: new Map(), paddocks: new Map() });

interface UsePaddocksAutosaveArgs {
  userProfile: PartialUserProfile | undefined;
  /** Runs a write after every earlier one (PaddocksView's queue). */
  enqueue: (job: () => Promise<void>) => void;
  latestFarm: (farmId: string) => IFarm | undefined;
  latestZone: (zoneId: string) => IZone | null;
  upsertFarm: (args: {
    farm: IFarm;
    zones: IZone[];
  }) => Promise<IFarmWriteResponse>;
  /** After a save round: how many records failed (0 when all saved). */
  onSaved: (failed: number) => void;
}

/**
 * The Paddocks grid's autosave, matching Planning's (OP-2481): edits across
 * any farms and paddocks show at once and are saved together
 * INPUT_DOCUMENT_SAVE_DEBOUNCE_MS after the last one, or straight away when
 * the lens closes or a bulk action runs.
 *
 * A paddock lives in its farm's document and its name, area and unit also
 * change its zone, so each farm's edits — its own fields, its paddocks and
 * their zones — are one Farm/Upsert (buildFarmSave), built from the latest
 * cached farm when it runs. Failed farms' edits stay staged, still shown,
 * and go again on the next save.
 */
export const usePaddocksAutosave = ({
  userProfile,
  enqueue,
  latestFarm,
  latestZone,
  upsertFarm,
  onSaved,
}: UsePaddocksAutosaveArgs) => {
  // Staged edits, and those being saved — both are overlaid on the server
  // values so a field never shows its old value mid-save.
  const stagedRef = useRef<Edits>(emptyEdits());
  const savingRef = useRef<Edits>(emptyEdits());
  const timerRef = useRef<number | null>(null);
  const [, setVersion] = useState(0);
  const rerender = useCallback(() => setVersion((v) => v + 1), []);

  // The latest callbacks, so the timer and unmount flush never use stale ones.
  const argsRef = useRef({
    userProfile,
    enqueue,
    latestFarm,
    latestZone,
    upsertFarm,
    onSaved,
  });
  argsRef.current = {
    userProfile,
    enqueue,
    latestFarm,
    latestZone,
    upsertFarm,
    onSaved,
  };

  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const batch = stagedRef.current;
    if (!batch.farms.size && !batch.paddocks.size) return;
    stagedRef.current = emptyEdits();
    savingRef.current = batch;
    rerender();

    const args = argsRef.current;
    args.enqueue(async () => {
      const failed = emptyEdits();

      // One save per farm touched: its own fields plus its paddocks' edits.
      const paddocksByFarm = new Map<string, Map<string, PaddockPatch>>();
      batch.paddocks.forEach((edit, paddockId) => {
        const patches = paddocksByFarm.get(edit.farmId) ?? new Map();
        patches.set(paddockId, edit.patch);
        paddocksByFarm.set(edit.farmId, patches);
      });
      const farmIds = new Set([
        ...Array.from(batch.farms.keys()),
        ...Array.from(paddocksByFarm.keys()),
      ]);

      for (const farmId of Array.from(farmIds)) {
        const farm = args.latestFarm(farmId);
        if (!farm) continue; // deleted meanwhile
        const paddockPatches = paddocksByFarm.get(farmId) ?? new Map();
        try {
          await args.upsertFarm(
            buildFarmSave(
              farm,
              batch.farms.get(farmId),
              paddockPatches,
              args.latestZone,
              args.userProfile
            )
          );
        } catch {
          const farmPatch = batch.farms.get(farmId);
          if (farmPatch) failed.farms.set(farmId, farmPatch);
          paddockPatches.forEach((patch, paddockId) =>
            failed.paddocks.set(paddockId, { farmId, patch })
          );
        }
      }

      // Failed edits go back under anything typed since, to retry later.
      failed.farms.forEach((patch, farmId) =>
        stagedRef.current.farms.set(farmId, {
          ...patch,
          ...stagedRef.current.farms.get(farmId),
        })
      );
      failed.paddocks.forEach((edit, paddockId) =>
        stagedRef.current.paddocks.set(paddockId, {
          farmId: edit.farmId,
          patch: {
            ...edit.patch,
            ...stagedRef.current.paddocks.get(paddockId)?.patch,
          },
        })
      );
      savingRef.current = emptyEdits();
      rerender();
      args.onSaved(failed.farms.size + failed.paddocks.size);
    });
  }, [rerender]);

  const schedule = useCallback(() => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(
      flush,
      INPUT_DOCUMENT_SAVE_DEBOUNCE_MS
    );
    rerender();
  }, [flush, rerender]);

  const stageFarm = useCallback(
    (farmId: string, patch: FarmPatch) => {
      const { farms } = stagedRef.current;
      farms.set(farmId, { ...farms.get(farmId), ...patch });
      schedule();
    },
    [schedule]
  );

  const stagePaddock = useCallback(
    (farmId: string, paddockId: string, patch: PaddockPatch) => {
      const { paddocks } = stagedRef.current;
      paddocks.set(paddockId, {
        farmId,
        patch: { ...paddocks.get(paddockId)?.patch, ...patch },
      });
      schedule();
    },
    [schedule]
  );

  /** The farm's values as shown: saved, then saving, then staged edits. */
  const farmValues = (farm: IFarm): FarmFormValues => ({
    ...farmFormValues(farm),
    ...savingRef.current.farms.get(farm.id),
    ...stagedRef.current.farms.get(farm.id),
  });

  const paddockValues = (paddock: IPaddock): PaddockFormValues => ({
    ...paddockFormValues(paddock),
    ...savingRef.current.paddocks.get(paddock.paddockId)?.patch,
    ...stagedRef.current.paddocks.get(paddock.paddockId)?.patch,
  });

  const stagedCount =
    stagedRef.current.farms.size + stagedRef.current.paddocks.size;

  // Leaving the lens saves what's staged rather than dropping it.
  useEffect(() => () => flush(), [flush]);

  return {
    stageFarm,
    stagePaddock,
    flush,
    farmValues,
    paddockValues,
    stagedCount,
  };
};

export default usePaddocksAutosave;
